/**
 * UnityCameraControls: Unity Editor Scene View-style camera navigation.
 *
 * Controls:
 *   Right-click + WASD  = Fly forward/left/back/right (local axes)
 *   Right-click + Q/E   = Fly down/up (world Y)
 *   Right-click + Shift  = Speed boost (3x)
 *   Right-click drag     = Freelook (FPS-style yaw/pitch)
 *   Middle-click drag    = Pan (camera local XY)
 *   Scroll wheel         = Scrub the timeline (same as every other view)
 *   Shift + scroll       = Dolly forward/backward
 *   Alt + Left-click     = Orbit around focus point
 *   Alt + Right-click    = Zoom toward/away from focus
 *
 * All camera mutations are imperative (refs + useFrame) for 60 fps.
 */

import { useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useEditorStore } from '../../store/editorStore';

// ── Config ───────────────────────────────────────────────────────────────────

const MOVE_SPEED = 5;
const LOOK_SENSITIVITY = 0.002;
const PAN_SENSITIVITY = 0.005;
const ZOOM_SPEED = 2;
const SHIFT_MULTIPLIER = 3;

// ── Reusable temp vectors (avoid per-frame allocations) ──────────────────────

const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

// ── Component ────────────────────────────────────────────────────────────────

export default function UnityCameraControls() {
  const { camera, gl } = useThree();

  // Input state (all refs — no React re-renders)
  const keysPressed = useRef<Set<string>>(new Set());
  const isRightMouseDown = useRef(false);
  const isMiddleMouseDown = useRef(false);
  const isLeftMouseDown = useRef(false);
  const isAltDown = useRef(false);

  // Camera orientation as Euler angles (YXZ order, avoids gimbal lock for FPS)
  const yaw = useRef(0);
  const pitch = useRef(0);

  // Orbit target for Alt+Left-click orbit
  const orbitTarget = useRef(new THREE.Vector3(0, 1.5, -10));

  // Track whether we've initialised yaw/pitch from the camera
  const initialised = useRef(false);

  // ── Initialise yaw/pitch from current camera rotation on mount ──────────

  useEffect(() => {
    const euler = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
    yaw.current = euler.y;
    pitch.current = euler.x;
    initialised.current = true;
  }, [camera]);

  // ── Event listeners ─────────────────────────────────────────────────────

  useEffect(() => {
    const canvas = gl.domElement;

    // ── Pointer Down ──────────────────────────────────────────────────
    const onPointerDown = (e: PointerEvent) => {
      isAltDown.current = e.altKey;

      if (e.button === 2) {
        // Right-click
        isRightMouseDown.current = true;
        canvas.style.cursor = 'none';

        // Sync yaw/pitch from current camera in case preset just switched
        const euler = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
        yaw.current = euler.y;
        pitch.current = euler.x;

        // Tell the rest of the app that fly mode is active (suppresses WASDQE shortcuts)
        useEditorStore.getState().setFlyModeActive(true);
      }
      if (e.button === 1) {
        // Middle-click
        isMiddleMouseDown.current = true;
        e.preventDefault();
      }
      if (e.button === 0) {
        // Left-click
        isLeftMouseDown.current = true;
      }
    };

    // ── Pointer Move ──────────────────────────────────────────────────
    const onPointerMove = (e: PointerEvent) => {
      const dx = e.movementX || 0;
      const dy = e.movementY || 0;
      if (dx === 0 && dy === 0) return;

      // Right-click freelook (FPS-style)
      if (isRightMouseDown.current && !isAltDown.current) {
        yaw.current -= dx * LOOK_SENSITIVITY;
        pitch.current -= dy * LOOK_SENSITIVITY;
        // Clamp pitch to avoid flipping
        pitch.current = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, pitch.current));

        const q = new THREE.Quaternion();
        q.setFromEuler(new THREE.Euler(pitch.current, yaw.current, 0, 'YXZ'));
        camera.quaternion.copy(q);
      }

      // Middle-click pan
      if (isMiddleMouseDown.current && !isAltDown.current) {
        camera.getWorldDirection(_forward);
        _right.crossVectors(_forward, _up).normalize();
        const localUp = new THREE.Vector3().crossVectors(_right, _forward).normalize();

        camera.position.addScaledVector(_right, -dx * PAN_SENSITIVITY);
        camera.position.addScaledVector(localUp, dy * PAN_SENSITIVITY);
      }

      // Alt + Left-click = Orbit around focus point
      if (isAltDown.current && isLeftMouseDown.current) {
        const offset = camera.position.clone().sub(orbitTarget.current);
        const spherical = new THREE.Spherical().setFromVector3(offset);

        spherical.theta -= dx * LOOK_SENSITIVITY * 2;
        spherical.phi -= dy * LOOK_SENSITIVITY * 2;
        spherical.phi = Math.max(0.05, Math.min(Math.PI - 0.05, spherical.phi));

        offset.setFromSpherical(spherical);
        camera.position.copy(orbitTarget.current).add(offset);
        camera.lookAt(orbitTarget.current);

        // Sync yaw/pitch from the new lookAt orientation
        const euler = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
        yaw.current = euler.y;
        pitch.current = euler.x;
      }

      // Alt + Right-click = Zoom toward/away from focus
      if (isAltDown.current && isRightMouseDown.current) {
        camera.getWorldDirection(_forward);
        camera.position.addScaledVector(_forward, -dy * 0.05);
      }
    };

    // ── Pointer Up (on both canvas and window for safety) ─────────────
    const onPointerUp = (e: PointerEvent) => {
      if (e.button === 2) {
        isRightMouseDown.current = false;
        canvas.style.cursor = '';
        keysPressed.current.clear();
        useEditorStore.getState().setFlyModeActive(false);
      }
      if (e.button === 1) isMiddleMouseDown.current = false;
      if (e.button === 0) isLeftMouseDown.current = false;
      isAltDown.current = e.altKey;
    };

    // ── Scroll wheel = dolly ──────────────────────────────────────────
    const onWheel = (e: WheelEvent) => {
      // Don't capture if Ctrl is held (snap-division cycling in useKeyboardShortcuts)
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      // Reviewer ask: in free-cam the wheel should SCRUB THE TIMELINE like it
      // does in every other view — it used to dolly the camera, which made the
      // free view the one place scrolling didn't move you through the song.
      // Dolly moved to Shift+wheel (and Alt+right-drag still zooms).
      if (!e.shiftKey) {
        const s = useEditorStore.getState();
        if (s.playback.playing) return;                // don't fight playback
        const dir = e.deltaY > 0 ? -1 : 1;             // wheel up = forward
        const step = s.scrollStep || 0.25;
        s.setCurrentBeat(Math.max(0, s.playback.currentBeat + dir * step));
        return;
      }
      camera.getWorldDirection(_forward);
      const delta = e.deltaY > 0 ? -ZOOM_SPEED * 0.15 : ZOOM_SPEED * 0.15;
      camera.position.addScaledVector(_forward, delta);
    };

    // ── Key down/up — WASDQE only captured when right-click is held ──
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (!isRightMouseDown.current) return;

      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'q', 'e'].includes(key)) {
        keysPressed.current.add(key);
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === 'Shift') {
        keysPressed.current.add('shift');
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      keysPressed.current.delete(key);
      if (e.key === 'Shift') keysPressed.current.delete('shift');
    };

    // ── Suppress context menu on canvas ──────────────────────────────
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    // ── Attach ───────────────────────────────────────────────────────
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointerup', onPointerUp);   // catch releases outside canvas
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);

      // Clean up fly-mode flag in case component unmounts while RMB held
      canvas.style.cursor = '';
      if (isRightMouseDown.current) {
        useEditorStore.getState().setFlyModeActive(false);
        isRightMouseDown.current = false;
      }
    };
  }, [camera, gl]);

  // ── Per-frame update ──────────────────────────────────────────────────────

  useFrame((_, delta) => {
    if (!initialised.current) return;
    const dt = Math.min(delta, 0.1); // clamp to avoid huge jumps on tab switch

    // ── Handle "Save Custom Camera" request from sidebar ──────────────
    const store = useEditorStore.getState();
    if (store.saveCustomCameraRequested) {
      const pos: [number, number, number] = [camera.position.x, camera.position.y, camera.position.z];
      const rot: [number, number, number] = [camera.rotation.x, camera.rotation.y, camera.rotation.z];
      store.saveCustomCamera(pos, rot);
    }

    // ── WASD + Q/E movement (only when right-click held) ─────────────
    if (isRightMouseDown.current) {
      const speed = MOVE_SPEED * dt * (keysPressed.current.has('shift') ? SHIFT_MULTIPLIER : 1);

      camera.getWorldDirection(_forward);
      _right.crossVectors(_forward, _up).normalize();

      if (keysPressed.current.has('w')) camera.position.addScaledVector(_forward, speed);
      if (keysPressed.current.has('s')) camera.position.addScaledVector(_forward, -speed);
      if (keysPressed.current.has('d')) camera.position.addScaledVector(_right, speed);
      if (keysPressed.current.has('a')) camera.position.addScaledVector(_right, -speed);
      if (keysPressed.current.has('e')) camera.position.y += speed;
      if (keysPressed.current.has('q')) camera.position.y -= speed;
    }
  });

  return null; // No JSX — purely imperative
}
